import { getApiUrl } from '@/utils/ajax';
import { ConfiguratorVisualizationRoomDto } from '@/services/configurator-visualization/configurator-visualization.types';
import {
    ShowroomCalculateRequest,
    ShowroomCalculateResponse,
    ShowroomData,
    ShowroomPathParams,
} from './showroom.types';

const API_BASE = '/api/showroom';

function pathFor(p: ShowroomPathParams): string {
    return `${API_BASE}/${encodeURIComponent(p.projectSlug)}/${p.unitId}/${encodeURIComponent(p.hash)}`;
}

// The showroom is anonymous: we deliberately call fetch directly (not the auth-aware request()
// helper) so no Authorization header / Redux culture is sent. The backend treats these endpoints
// as `[AllowAnonymous]`.
function showroomFetch(path: string, init?: RequestInit): Promise<Response> {
    return fetch(`${getApiUrl()}${path}`, {
        ...init,
        credentials: 'omit',
        headers: {
            ...(init?.headers ?? {}),
            ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        },
    });
}

export async function getShowroomData(params: ShowroomPathParams): Promise<ShowroomData> {
    const res = await showroomFetch(pathFor(params));
    if (res.status === 404) {
        throw new ShowroomNotFoundError(params);
    }
    if (!res.ok) {
        throw new Error(`Failed to fetch showroom data (HTTP ${res.status})`);
    }
    return res.json();
}

export async function calculateShowroom(
    params: ShowroomPathParams,
    request: ShowroomCalculateRequest
): Promise<ShowroomCalculateResponse> {
    const res = await showroomFetch(`${pathFor(params)}/calculate`, {
        method: 'POST',
        body: JSON.stringify(request),
    });
    if (res.status === 404) {
        throw new ShowroomNotFoundError(params);
    }
    if (!res.ok) {
        throw new Error(`Failed to calculate showroom (HTTP ${res.status})`);
    }
    return res.json();
}

// Showroom-scoped 3D visualization / mapping data — the anonymous mirror of the authenticated
// `getConfiguratorVisualization`. `showroomPath` is the pre-joined `{projectSlug}/{unitId}/{hash}`
// triplet (the same value used to build showroom image URLs), so we interpolate it directly like the
// image endpoints rather than re-encoding via pathFor(). A 404 means the unit simply has no
// visualization configured — return an empty list so callers get the same array contract as the
// authenticated endpoint (the hash was already validated by getShowroomData).
export async function getShowroomConfiguratorVisualization(
    showroomPath: string
): Promise<ConfiguratorVisualizationRoomDto[]> {
    const res = await showroomFetch(`${API_BASE}/${showroomPath}/visualization`);
    if (res.status === 404) {
        return [];
    }
    if (!res.ok) {
        throw new Error(`Failed to fetch showroom visualization (HTTP ${res.status})`);
    }
    return res.json();
}

export function getShowroomLandingPageImageUrl(
    params: ShowroomPathParams,
    imageId: number,
    options?: { thumbnail?: boolean }
): string {
    const suffix = options?.thumbnail ? '/thumbnail' : '';
    return `${getApiUrl()}${pathFor(params)}/landing-page-image/${imageId}${suffix}`;
}

export class ShowroomNotFoundError extends Error {
    constructor(public readonly params: ShowroomPathParams) {
        super(`Showroom not found for ${params.projectSlug}/${params.unitId}/${params.hash}`);
        this.name = 'ShowroomNotFoundError';
    }
}
